/**
 * RDAPToolSelector -- per-agent picker for RDAP-backed domain tools.
 *
 * AC-1442 (parent epic AC-1434). Mirrors `GoDaddyToolSelector.tsx` in shape
 * and styling, but is intentionally credential-free: RDAP servers are public
 * and require no API key, so this component does NOT take a `projectId`,
 * does NOT call any credentials endpoint, and renders no API-key affordance.
 * See Technical Design AC-1434 section 4.2 ("Frontend RDAPToolSelector") for
 * the full distinction.
 *
 * Data flow:
 *   1. On mount, fetch the available RDAP tool definitions from
 *      `apiService.getRDAPToolDefinitions()` (single source of truth, served
 *      by `GET /api/v1/tools/rdap/definitions`).
 *   2. Render each definition as a clickable card; selected state is derived
 *      from the `selectedTools` prop by matching `AgentTool.type === def.type`.
 *   3. Toggling a card calls `onToolsChange` with the updated `AgentTool[]`.
 *
 * Wiring (AC-1444): the parent (`AgentFormModal`) renders this component next
 * to `<GoDaddyToolSelector>`. Until that wiring lands, this component is
 * self-contained and reachable only via tests.
 */
import React, { useState, useEffect } from 'react';
import {
  Globe,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Code,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { AgentTool } from '../../types';
import apiService from '../../services/api';
import { RDAPToolDefinition, RecommendedSchema } from '../../types/rdap';

/**
 * Props for `RDAPToolSelector`.
 *
 * NO `projectId` -- RDAP is credential-free. If the parent (AC-1444) renders
 * all tool selectors with a uniform `projectId` prop and we want to avoid
 * special-casing the call site there, we can revisit; for now we follow the
 * Technical Design verbatim (TD section 4.2, line 433).
 *
 * `onUseRecommendedSchemas` (AC-1446): when provided, an inline banner
 * appears under any selected tool that ships `recommended_output_schemas`.
 * Clicking the banner calls this handler with the schemas, which the parent
 * (typically `AgentFormModal`) routes through its existing `onCreateSchema`
 * plumbing -- no new schema-creation pathway is introduced. Omit the prop
 * to disable the affordance entirely (e.g., in standalone test renders).
 */
export interface RDAPToolSelectorProps {
  selectedTools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
  onUseRecommendedSchemas?: (
    schemas: RecommendedSchema[]
  ) => Promise<void> | void;
}

const RDAPToolSelector: React.FC<RDAPToolSelectorProps> = ({
  selectedTools,
  onToolsChange,
  onUseRecommendedSchemas,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedExamples, setExpandedExamples] = useState<Set<string>>(
    new Set()
  );
  // AC-1446: track which tool's "Use recommended schema" button is in-flight
  // so we can disable it and show a spinner. Keyed by tool.type because the
  // user could (in theory) trigger multiple banners; in practice only one is
  // visible at a time per the current selector layout.
  const [creatingSchemaFor, setCreatingSchemaFor] = useState<string | null>(
    null
  );

  // Tool definitions fetched from the backend on mount. Single source of truth
  // for descriptions and examples (TD section 6.2). Loading and error states
  // are surfaced inline so the parent form does not need to coordinate.
  const [tools, setTools] = useState<RDAPToolDefinition[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [toolsError, setToolsError] = useState<string | null>(null);

  const toggleExample = (toolType: string) => {
    setExpandedExamples((prev) => {
      const next = new Set(prev);
      if (next.has(toolType)) {
        next.delete(toolType);
      } else {
        next.add(toolType);
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchToolDefinitions = async () => {
      try {
        setIsLoadingTools(true);
        setToolsError(null);
        const response = await apiService.getRDAPToolDefinitions();
        setTools(response.tools);
      } catch (error) {
        // Match GoDaddy's behavior: log to console for triage, surface a
        // human-readable inline error so the user knows the section is
        // unavailable without leaking error internals.
        // eslint-disable-next-line no-console
        console.error('Failed to fetch RDAP tool definitions:', error);
        setToolsError('Failed to load tool definitions');
      } finally {
        setIsLoadingTools(false);
      }
    };

    fetchToolDefinitions();
  }, []);

  const isToolSelected = (toolType: string): boolean => {
    return selectedTools.some((tool) => tool.type === toolType);
  };

  const handleToolToggle = (tool: RDAPToolDefinition) => {
    const isSelected = isToolSelected(tool.type);

    let updatedTools: AgentTool[];
    if (isSelected) {
      updatedTools = selectedTools.filter((t) => t.type !== tool.type);
    } else {
      // RDAP tool types (e.g., `rdap_check_availability`) are not yet listed
      // in the `AgentTool['type']` union in `types/index.ts` -- adding them is
      // AC-1444's wiring concern. We cast at the boundary so this component
      // stays self-contained. See TD section 4.2.
      const newTool: AgentTool = {
        type: tool.type as AgentTool['type'],
        name: tool.name,
        description: tool.description,
      };
      updatedTools = [...selectedTools, newTool];
    }

    onToolsChange(updatedTools);
  };

  if (isLoadingTools) {
    return (
      <div
        className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg"
        data-testid="rdap-tool-selector"
      >
        <div className="flex items-center space-x-2">
          <Loader2 className="w-4 h-4 animate-spin text-gray-500 dark:text-gray-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading tools...
          </p>
        </div>
      </div>
    );
  }

  if (toolsError) {
    return (
      <div
        className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
        data-testid="rdap-tool-selector"
      >
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to Load Tools
            </p>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              {toolsError}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Hide the section when the backend reports zero tools. This keeps the
  // agent form clean if RDAP support is ever feature-flagged off server-side.
  if (tools.length === 0) {
    return null;
  }

  // Count only RDAP tools in the selected summary so non-RDAP tools (e.g.,
  // `web_search`, `godaddy_*`) don't inflate the badge. The backend tool-type
  // namespace is `rdap_*` (TD section 2.3).
  const selectedRDAPCount = selectedTools.filter((t) =>
    t.type.startsWith('rdap_')
  ).length;

  return (
    <div className="space-y-3" data-testid="rdap-tool-selector">
      {/* Collapsible header -- mirrors GoDaddyToolSelector for visual parity. */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg p-1 -ml-1 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" />
        )}
        <Globe className="w-5 h-5 text-primary-600 mr-2" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white">
          Domain Availability Tools
        </h3>
        {selectedRDAPCount > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            {selectedRDAPCount} selected
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-3 pl-6">
          {tools.map((tool) => {
            const isSelected = isToolSelected(tool.type);
            const isExampleExpanded = expandedExamples.has(tool.type);

            return (
              <div
                key={tool.type}
                className={`border rounded-lg transition-all ${
                  isSelected
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark'
                }`}
              >
                {/* Clickable selection area */}
                <div
                  onClick={() => handleToolToggle(tool)}
                  className={`p-3 cursor-pointer ${
                    !isSelected
                      ? 'hover:bg-primary-50 dark:hover:bg-gray-800'
                      : ''
                  }`}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToolToggle(tool);
                    }
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected
                            ? 'border-blue-600 bg-primary-600'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark'
                        }`}
                      >
                        {isSelected && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          isSelected
                            ? 'text-blue-900 dark:text-blue-200'
                            : 'text-gray-900 dark:text-warm-white'
                        }`}
                      >
                        {tool.name}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isSelected
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {tool.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Optional input/example block -- shown only when defs include them. */}
                {(tool.input_description || tool.example_output) && (
                  <div className="px-3 pb-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExample(tool.type);
                      }}
                      className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      <Code className="w-3 h-3 mr-1" />
                      {isExampleExpanded ? 'Hide' : 'Show'} input &amp; example output
                      {isExampleExpanded ? (
                        <ChevronDown className="w-3 h-3 ml-1" />
                      ) : (
                        <ChevronRight className="w-3 h-3 ml-1" />
                      )}
                    </button>

                    {isExampleExpanded && (
                      <div className="mt-2 space-y-2">
                        {tool.input_description && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Input
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded">
                              {tool.input_description}
                            </p>
                          </div>
                        )}
                        {tool.example_output && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                              Example Output
                            </p>
                            <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                              {tool.example_output}
                            </pre>
                          </div>
                        )}
                        {tool.notes && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                            {tool.notes}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* AC-1446: One-click recommended-schema banner. Shown only
                    when (a) the tool is currently selected, (b) the tool
                    definition includes recommended schemas, and (c) the
                    parent supplied a handler. We deliberately keep the
                    banner unobtrusive (single inline button, no modal) so
                    it does not interrupt the agent-edit flow. */}
                {isSelected
                  && tool.recommended_output_schemas
                  && tool.recommended_output_schemas.length > 0
                  && onUseRecommendedSchemas && (
                    <div
                      className="px-3 pb-3 -mt-1"
                      data-testid={`rdap-recommended-schema-banner-${tool.type}`}
                    >
                      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <Sparkles
                            className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0"
                            aria-hidden="true"
                          />
                          <p className="text-xs text-purple-900 dark:text-purple-200 truncate">
                            Use recommended output schema for this tool
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={creatingSchemaFor === tool.type}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!onUseRecommendedSchemas) return;
                            const schemas = tool.recommended_output_schemas;
                            if (!schemas || schemas.length === 0) return;
                            try {
                              setCreatingSchemaFor(tool.type);
                              await onUseRecommendedSchemas(schemas);
                            } finally {
                              setCreatingSchemaFor(null);
                            }
                          }}
                          className="text-xs font-medium px-2 py-1 rounded bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white transition-colors flex-shrink-0"
                        >
                          {creatingSchemaFor === tool.type
                            ? 'Creating...'
                            : 'Use recommended schema'}
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RDAPToolSelector;
